import { Component, OnInit } from '@angular/core';
import { ThemeService } from '../../../core/theme/theme.service';
import { environment } from '../../../../environments/environment';
import { Theme } from '../../../core/theme/theme.model';

@Component({
  selector: 'app-theme-switcher',
  templateUrl: './theme-switcher.component.html',
  styleUrls: ['./theme-switcher.component.scss']
})
export class ThemeSwitcherComponent implements OnInit {
  isOpen = false;
  isDevelopment = !environment.production;
  availableThemes: string[] = ['default', 'greentech', 'bluesky'];
  currentTenant = 'default';
  currentTheme: Theme | null = null;
  customTenant = '';
  isLoading = false;

  constructor(private themeService: ThemeService) {}

  ngOnInit(): void {
    if (!this.isDevelopment) {
      return;
    }

    this.extractCurrentTenant();

    this.themeService.getCurrentTheme$().subscribe(theme => {
      this.currentTheme = theme;
      if (theme) {
        this.currentTenant = theme.clientId || 'default';
      }
    });

    const savedPosition = localStorage.getItem('theme-switcher-position');
    if (savedPosition) {
      const { top, right } = JSON.parse(savedPosition);
      const switcher = document.querySelector('.theme-switcher-container') as HTMLElement;
      if (switcher) {
        switcher.style.top = top;
        switcher.style.right = right;
      }
    }
  }

  private extractCurrentTenant(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get('tenant');
    if (tenantParam) {
      this.currentTenant = tenantParam;
    }
  }

  toggleSwitcher(): void {
    this.isOpen = !this.isOpen;
  }

  async switchTheme(tenantId: string): Promise<void> {
    this.isLoading = true;
    try {
      await this.themeService.loadAndApplyTheme(tenantId);
      this.currentTenant = tenantId;
      this.updateUrlWithTenant(tenantId);
    } catch (error) {
      console.error('Failed to switch theme:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async loadCustomTheme(): Promise<void> {
    if (!this.customTenant.trim()) {
      return;
    }
    await this.switchTheme(this.customTenant.trim());
  }

  private updateUrlWithTenant(tenantId: string): void {
    const url = new URL(window.location.href);
    if (tenantId === 'default') {
      url.searchParams.delete('tenant');
    } else {
      url.searchParams.set('tenant', tenantId);
    }
    window.history.replaceState({}, '', url.toString());
  }

  reloadTheme(): void {
    this.switchTheme(this.currentTenant);
  }

  onDragStart(event: DragEvent): void {
    const target = event.target as HTMLElement;
    const container = target.closest('.theme-switcher-container') as HTMLElement;
    if (container) {
      const rect = container.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const offsetY = event.clientY - rect.top;
      event.dataTransfer?.setData('text/plain', JSON.stringify({ offsetX, offsetY }));
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    const data = event.dataTransfer?.getData('text/plain');
    if (data) {
      const { offsetX, offsetY } = JSON.parse(data);
      const container = document.querySelector('.theme-switcher-container') as HTMLElement;
      if (container) {
        const newTop = Math.max(0, event.clientY - offsetY);
        const newRight = Math.max(0, window.innerWidth - event.clientX - (container.offsetWidth - offsetX));

        container.style.top = `${newTop}px`;
        container.style.right = `${newRight}px`;

        localStorage.setItem('theme-switcher-position', JSON.stringify({
          top: `${newTop}px`,
          right: `${newRight}px`
        }));
      }
    }
  }
}